import React, {
  Dispatch,
  FormEvent,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  Grid,
  Typography,
} from '@material-ui/core';
import {
  DefaultRootState,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  useHistory,
  useParams,
} from 'react-router';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import useStyles from '../../../components/paper/MainPaper.style';
import WhitePaper from '../../../components/paper/WhitePaper';
import GreyTextInput from '../../../components/form/GreyTextInput';
import UploadFileButton from '../../../components/form/UploadFileButton';
import {
  getBooks,
  postBook,
  putBook,
  resetStatus,
} from '../../../store/book/actions';
import {
  BookState,
  IBook,
} from '../../../store/book/types';
import { routeBooks } from '../../../constants/routes';
import Breadcrumb from '../../../components/common/Breadcrumb';
import { NotificationContext } from '../../../contexts/NotificationContext';
import getFileName from '../../../utils/getFileName';
import DatePicker from '../../../components/form/DatePicker';
import TextareaInput from '../../../components/form/TextareaInput';
import formatDateWithTimezone from '../../../utils/formatDate';
import SwitchComponent from '../../../components/form/SwitchComponent';

type Props = {};

type Picture = {
  file?: File | string,
  preview: string,
  fileName: string
}

const FormBookPage: React.FC<Props> = () => {
  const { t } = useTranslation(['backoffice', 'common']);
  const classes = useStyles();
  const history = useHistory();
  const dispatch: Dispatch<any> = useDispatch();
  const slug: { id: string } = useParams();
  const { setNotification } = useContext(NotificationContext);

  const {
    sending,
    books,
    error,
    status,
  } = useSelector<DefaultRootState, BookState>(
    (state: any) => state.bookStore,
  );

  useEffect(() => {
    if (books.length === 0) dispatch(getBooks());
  }, []);

  const bookId = slug.id;
  let book: IBook | undefined;

  if (bookId) {
    book = books.find((p: IBook) => p.id === bookId);
  }

  const getValuesFromBook = (from: IBook | undefined) => ({
    title: from?.title ?? '',
    link: from?.link ?? '',
    release_date: from?.release_date ? new Date(from.release_date) : new Date(),
    authors: from?.authors ?? '',
    is_orderable: from?.is_orderable ?? true,
  });

  const [values, setValues] = useState<Pick<IBook, 'title' | 'link' | 'release_date' | 'is_orderable' | 'authors'>>(
    getValuesFromBook(book),
  );

  const [formErrors, setFormErrors] = useState({
    title: '',
    link: '',
    release_date: '',
    authors: '',
    picture: '',
    is_orderable: true,
  });

  const getPictureFromBook = (from: IBook | undefined) => ({
    file: from?.file_path ?? '',
    preview: from?.file_path ?? '',
    fileName: getFileName(from?.file_path) ?? '',
  });

  const [picture, setPicture] = useState<Picture>(getPictureFromBook(book));

  useEffect(() => {
    book = books.find((p: IBook) => p.id === bookId);
    setValues(getValuesFromBook(book));
    setPicture(getPictureFromBook(book));
  }, [books]);

  const handleFormAction = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    let formError = false;

    if (values.title === '') {
      formErrors.title = t('book.form.titleRequired');
      formError = true;
    }

    if (values.release_date === null) {
      formErrors.release_date = t('book.form.releaseDateRequired');
      formError = true;
    }

    if (picture.file === '') {
      formErrors.picture = t('book.form.pictureRequired');
      formError = true;
    }

    if (values.authors === '') {
      formErrors.authors = t('book.form.authorsRequired');
      formError = true;
    }

    setFormErrors({ ...formErrors });

    if (!formError) {
      const content = {
        title: values.title,
        link: values.link,
        release_date: formatDateWithTimezone(values.release_date as Date),
        authors: values.authors,
        is_orderable: values.is_orderable,
      };

      if (bookId) {
        dispatch(putBook(bookId, content, picture.file));
      } else {
        dispatch(postBook(content, picture.file as File));
      }
    }
  };

  const handleChange = (prop: keyof Partial<IBook>, value: any) => {
    setValues({ ...values, [prop]: value });
    setFormErrors({ ...formErrors, [prop]: '' });
  };

  const uploadFile = (file: File) => {
    if (file) {
      setPicture({ file, preview: URL.createObjectURL(file), fileName: file.name });
      setFormErrors({ ...formErrors, picture: '' });
    }
  };

  useEffect(() => {
    if (status === 'ERROR') {
      setNotification({ isOpened: true, severity: 'error', message: error || '' });
    }

    if (status === 'SUCCESS') {
      setNotification({ isOpened: true, severity: 'success', message: t('book.form.success') });
      history.push(routeBooks);
    }
    if (status !== 'INIT') dispatch(resetStatus());
  }, [status]);

  return (
    <>
      <Breadcrumb
        title={t('title.books')}
        baseUrl={routeBooks}
        link={book && book.title}
      />
      <Typography variant="h1" className={classes.title}>
        {book && book.title}
      </Typography>
      <form onSubmit={handleFormAction}>
        <Grid container spacing={3}>
          <Grid item id="form-grid-item" xs={12} md={9}>
            <WhitePaper title={t('book.form.subtitle')}>
              <GreyTextInput
                type="text"
                value={values.title}
                label={t('book.form.title')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChange('title', event.target.value)}
                error={formErrors.title !== ''}
                helperText={formErrors.title}
              />
              <DatePicker
                value={values.release_date}
                onChange={(date: MaterialUiPickersDate) => handleChange('release_date', date)}
                error={formErrors.release_date !== ''}
                helperText={formErrors.release_date}
                label={t('book.form.release_date')}
              />
              <GreyTextInput
                type="text"
                value={values.link}
                label={t('book.form.link')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChange('link', event.target.value)}
              />
              <TextareaInput
                value={values.authors}
                label={t('book.form.authors')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChange('authors', event.target.value)}
                error={formErrors.authors !== ''}
                helperText={formErrors.authors}
              />
              <SwitchComponent
                value={values.is_orderable}
                onChange={(e: any) => handleChange('is_orderable', e.target.checked)}
                label={values.is_orderable ? t('book.form.is_orderable') : t('book.form.is_not_orderable')}
                name="is_orderable"
              />
            </WhitePaper>
            <br />
            <WhitePaper title={t('book.form.subtitlePicture')}>
              {picture.preview && (
                <img
                  src={picture.preview}
                  alt={picture.fileName}
                  width="auto"
                  height={115}
                  className={classes.preview}
                />
              )}
              <UploadFileButton
                uploadFile={uploadFile}
                label={t('common.addPicture')}
                error={formErrors.picture !== ''}
                helperText={formErrors.picture}
              />
            </WhitePaper>
          </Grid>
          <Grid item id="validation-grid-item" xs={12} md={3}>
            <Button
              variant="contained"
              color="secondary"
              type="submit"
              disabled={sending}
            >
              <Typography variant="button">
                {t('common.save')}
              </Typography>
              {
                sending
                && (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                    color="primary"
                  />
                )
              }
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default FormBookPage;
